import React, { useContext, useState } from 'react';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate } from 'react-router-dom';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import TextInput from '../../common/TextInput/TextInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Links from '../../common/Widgets/Links';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useErrorBoundary } from 'react-error-boundary';

export type CustomerSelfRegistrationFields = {
  email: string;
};
export type CustomerSelfRegistrationErrors = {
  email: string;
};

const initialFormValues: CustomerSelfRegistrationFields = {
  email: ''
};

const initialErrors: CustomerSelfRegistrationErrors = {
  email: ''
};

const CustomerSelfRegistration = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const [errors, setErrors] =
    useState<CustomerSelfRegistrationErrors>(initialErrors);
  const [formValues, setFormValues] =
    useState<CustomerSelfRegistrationFields>(initialFormValues);

  const [showModalPopup, setShowModalPopup] = useState(false);

  const { site } = useContext(SiteContext);

  const { brand } = site;

  const { brandDisplayFullName } = brand;

  const { email } = formValues;

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const goBack = (): void => {
    navigate(-1);
  };

  const validateCustomerSelfRegistrationDetails = (): boolean => {
    let isValid = true;

    const error: CustomerSelfRegistrationErrors = {
      ...initialErrors
    };
    if (CommonValidation.isNotEmpty(email)) {
      if (!CommonValidation.isValidEmail(email)) {
        error.email = 'Email address is invalid';
        isValid = false;
      }
    } else {
      isValid = false;
      error.email = 'Email address is required';
    }
    setErrors(error);
    return isValid;
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const sendCustomerSelfRegistrationEmail = async () => {
    const controller = new AbortController();

    const customerSelfRegistraionSuccess = (successfulResponse: boolean) => {
      if (successfulResponse) {
        setShowModalPopup(true);
      } else {
        console.log('Failed to send customer self registration email');
      }
    };

    await new CarInsuranceApi().CustomerSelfRegistrationEmail(
      brand.tiaProductCode,
      site.user.insuranceDealerId ?? 0,
      email,
      site.user.token,
      customerSelfRegistraionSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onContinueClick = () => {
    const isValid = validateCustomerSelfRegistrationDetails();
    if (isValid) {
      sendCustomerSelfRegistrationEmail();
    } else {
      console.log('Invalid email');
    }
  };

  return (
    <div className='customer-self-registration'>
      <Links
        id='back-link'
        title=''
        items={[{ to: '', text: 'Back', onClick: goBack }]}
        icon={chevronLeftComponent}
      />
      <h2>
        Customer Self-registration for Excess Return and Driveaway Insurance
      </h2>
      <p>
        It’s now possible for Dealers to send customers a digital application to
        apply for Excess Return and Driveaway Insurance. Simply enter your
        customer’s email address below and tell the customer to look out for an
        email from {brandDisplayFullName} Insurance to register and take
        advantage of these complimentary products.
      </p>
      <p>Benefits of sending digitally</p>
      <ul>
        <li>Supports contactless & digital journey</li>
        <li>Customer is responsible for the details entered</li>
        <li>Removes FCA compliance from the Dealer</li>
        <li>
          Application will appear in Dealer Admin once customer has submitted
          their details
        </li>
      </ul>
      <p>
        Dealer to complete registration number and start date before handover .
      </p>
      <div className='one-col'>
        <div className='control-label'>Customer's email address:</div>
        <div className='control'>
          <TextInput
            placeholder='Email address'
            name='email'
            className='input-text w250'
            value={email}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              onTextChange(e, 'email')
            }
            error={errors.email}
          />
        </div>
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={goBack}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btn-continue'
          text='Send'
          onClick={onContinueClick}
          additionalClassNames='btn-continue'
        />
      </div>
      <Modal
        role='modal-popup'
        isOpen={showModalPopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={() => {
          setShowModalPopup(false);
        }}>
        <ModalHeader
          toggle={() => {
            setShowModalPopup(false);
          }}></ModalHeader>
        <ModalBody>
          <h4>Thank you</h4>
          <p className='m1-top'>Email has been sent to {email}.</p>
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='confirmation-modal-btn-continue'
            text='Continue'
            additionalClassNames='btn-ok'
            onClick={() => {
              setShowModalPopup(false);
            }}
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CustomerSelfRegistration;
